import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchQuery, graphql, useFragment } from "react-relay";

import { faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { addMinutes, getMinutes, startOfMinute } from "date-fns";
import { noop } from "lodash";

import type { MeetWithUser } from "~/calendar/BasePanel/MeetWith";
import {
	type Event,
	type MeetingCostSettings,
	MeetWithContext,
	type MeetWithContextType,
} from "~/calendar/CalendarPage";
import EventForm, { eventFormDetails, type EventFormResult } from "~/calendar/EventForm";
import { EventFormContext, type EventFormContextType } from "~/calendar/EventForm/EventFormContext";
import useDateTimeState from "~/calendar/hooks/useDateTimeState";
import useDeleteEvent, { type EventToDelete } from "~/calendar/hooks/useDeleteEvent";
import usePreferredTimezone from "~/calendar/hooks/usePreferredTimezone";
import useRecurrenceOptions from "~/calendar/hooks/useRecurrenceOptions";
import {
	calculateDateToAvoidNoMeetingDay,
	calculateDateToAvoidShortNoticeMeeting,
	calculateSpeedyMeetingDates,
} from "~/calendar/utils/meetingGuidelinesUtils";
import { ViewportContext } from "~/common";
import { confirm } from "~/lib/prompts";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { inCalendarMode } from "~/lib/utils";
import { environment } from "~/models";
import { scrollToImaginaryEvent } from "~/ui/calendar/utils/scrolling";
import { IconButton, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import WeekPreview from "./WeekPreview";

import type { EventFormDetailQuery } from "~/__generated__/EventFormDetailQuery.graphql";
import type { EventFormModal_viewer$key } from "~/__generated__/EventFormModal_viewer.graphql";
import type { EventFormModalWithoutViewerQuery } from "~/__generated__/EventFormModalWithoutViewerQuery.graphql";

import styles from "./EventFormModal.module.css";

const viewerFragment = graphql`
	fragment EventFormModal_viewer on Viewer {
		hasAccessToScheduledMeetingRecordings
		...EventForm_viewer
		...usePreferredTimezone_viewer
		...WeekPreview_viewer

		remainingMeetingRecordingCredits

		account {
			extensionSettings {
				meetingCostEnabled
				meetingCostEnforceWorkspaceSettings
				meetingCostUnit
				meetingCostAvgSalary
				meetingAttendeesEnabled
				maxRecommendedAttendees
				attendeeWeeklyMeetingHoursWarningEnabled
				attendeeWeeklyMeetingHoursLimit
				noMeetingDaysEnabled
				noMeetingDay
				shortNoticeEnabled
				shortNoticeNumHours
				finiteRecurringMeetingsEnabled
				breaksBetweenMeetingsEnabled
				breaksBetweenMeetingsBehaviour
				promptWhenSavingEnabled
				promptWhenSavingGuidelinesUrl
			}
			billingAccount {
				hasAccessToMeetingGuidelines: hasAccessToFeature(feature: meeting_guidelines)
				hasAccessToMeetingCost: hasAccessToFeature(feature: meeting_cost)
				hasAccessToMeetingRecorderFeature: hasAccessToFeature(feature: meeting_recorder)
			}
		}

		meetingGuidelines {
			meetingCostEnabled
			meetingCostUnit
			meetingCostAvgSalary
			meetingAttendeesEnabled
			maxRecommendedAttendees
			attendeeWeeklyMeetingHoursWarningEnabled
			attendeeWeeklyMeetingHoursLimit
			noMeetingDaysEnabled
			noMeetingDay
			shortNoticeEnabled
			shortNoticeNumHours
			finiteRecurringMeetingsEnabled
			breaksBetweenMeetingsEnabled
			breaksBetweenMeetingsBehaviour
			promptWhenSavingEnabled
			promptWhenSavingGuidelinesUrl
		}

		settings {
			timezonePreferences {
				preferredTimezone {
					timezone {
						name
					}
					userLabel
				}
				extraTimezones {
					timezone {
						name
					}
					userLabel
				}
			}
		}
	}
`;

interface EventFormModalProps {
	viewer: EventFormModal_viewer$key;
	onFormFinish: (result: EventFormResult) => void;
	eventToEdit?: Pick<Event, "guid" | "title" | "start" | "end" | "isAllDay"> & EventToDelete;
	/** A way to pre-fill the form. Ignored if `eventToEdit` is present.
	 * @description If `initialData.start` is present and `initialData.end` is not, the form will adjust the time according to
	 * 	Meeting Guidelines. If both are present, those exact times will be used.
	 */
	initialData?: {
		start?: Date;
		/** Ignored if `initialData.start` is not present. */
		end?: Date;
		isAllDay?: boolean;
		repeat?: EventFormContextType["repeatState"];

		/** `EventForm` already pre-fills the title if there are only two participants (i.e. a 1-on-1).
		 * Don't use `initialData.title` if that's your case.
		 */
		title?: string;
		purpose?: string;
		participants?: MeetWithUser[];
	};
}

export default function EventFormModal({
	onFormFinish: onFormFinishProp,
	viewer: viewerKey,
	eventToEdit,
	initialData,
}: EventFormModalProps) {
	const { isMobile } = useContext(ViewportContext);
	const viewer = useFragment(viewerFragment, viewerKey);

	const meetingGuidelinesSettings =
		useFeatureIsOn("extension-prompts") && viewer.account.billingAccount.hasAccessToMeetingGuidelines
			? viewer.meetingGuidelines
			: null;
	const meetingCostSettings: MeetingCostSettings =
		useFeatureIsOn("extension-prompts") &&
		viewer.account.billingAccount.hasAccessToMeetingCost &&
		viewer.account.billingAccount.hasAccessToMeetingGuidelines &&
		viewer.meetingGuidelines
			? {
					meetingCostEnabled: viewer.meetingGuidelines.meetingCostEnabled,
					meetingCostEnforceWorkspaceSettings: viewer.account.extensionSettings.meetingCostEnforceWorkspaceSettings,
					meetingCostUnit: viewer.account.extensionSettings.meetingCostUnit,
					meetingCostAvgSalary: viewer.meetingGuidelines.meetingCostAvgSalary,
				}
			: null;

	const [eventAdditional, setEventAdditional] = useState<EventFormDetailQuery["response"]["event"]>(null);
	const [additionalLoading, setAdditionalLoading] = useState(false);

	const [meetWithParticipants, setMeetWithParticipants] = React.useState<MeetWithUser[]>(
		!eventToEdit && initialData?.participants ? initialData.participants : [],
	);

	const [hasEdits, _setHasEdits] = useState(false);
	const setHasEdits = useCallback(() => _setHasEdits(true), [_setHasEdits]);

	const onFormFinish = useCallback(
		async (result: EventFormResult) => {
			if (result.type === "cancelled") {
				if (hasEdits) {
					const okayToClose = await confirm("Changes you've made so far will not be saved.", {
						yesText: "Discard",
						noText: "Keep editing",
						simple: true,
						title: "Discard unsaved changes?",
					});

					if (!okayToClose) {
						// TODO: analytics: Close prevented
						return;
					}
				}
			}

			return onFormFinishProp(result);
		},
		[hasEdits, onFormFinishProp],
	);

	const onCancel = useCallback(() => onFormFinish({ type: "cancelled" }), [onFormFinish]);

	const deleteEvent = useDeleteEvent(eventToEdit);
	const onDeleteEvent = useCallback(async () => {
		const deletedEventGuid = await deleteEvent();
		if (deletedEventGuid) {
			onFormFinish({ type: "deleted", event: { eventGuid: deletedEventGuid } });
		}
	}, [deleteEvent, onFormFinish]);

	const [eventFormTitle, setEventFormTitle] = useState<string>(initialData?.title ?? "");
	const [eventFormPurpose, setEventFormPurpose] = useState<string>(initialData?.purpose ?? "");
	const { workingTimezone, systemToUserTime } = usePreferredTimezone(viewer);

	const defaultTimeState = useCallback(() => {
		const now = startOfMinute(systemToUserTime(initialData?.start ?? new Date()));

		if (initialData?.start) {
			if (initialData?.end) {
				return {
					start: now,
					end: startOfMinute(systemToUserTime(initialData.end)),
					isAllDay: Boolean(initialData.isAllDay),
					timezone: workingTimezone,
				} satisfies EventFormContextType["timeState"];
			}
		}

		const nextQuarterHour = addMinutes(now, 15 - (getMinutes(now) % 15));
		const startDate = nextQuarterHour;

		let guidelinesDates = calculateSpeedyMeetingDates(
			meetingGuidelinesSettings?.breaksBetweenMeetingsEnabled,
			meetingGuidelinesSettings?.breaksBetweenMeetingsBehaviour,
			startDate,
		);

		guidelinesDates = calculateDateToAvoidNoMeetingDay(
			meetingGuidelinesSettings?.noMeetingDaysEnabled,
			meetingGuidelinesSettings?.noMeetingDay,
			guidelinesDates.newStartDate,
			guidelinesDates.newEndDate,
		);

		guidelinesDates = calculateDateToAvoidShortNoticeMeeting(
			meetingGuidelinesSettings?.shortNoticeEnabled,
			meetingGuidelinesSettings?.shortNoticeNumHours,
			guidelinesDates.newStartDate,
			guidelinesDates.newEndDate,
		);

		return {
			start: guidelinesDates.newStartDate,
			end: guidelinesDates.newEndDate,
			isAllDay: false,
			timezone: workingTimezone,
		} satisfies EventFormContextType["timeState"];
	}, [initialData, meetingGuidelinesSettings, workingTimezone, systemToUserTime]);
	const [eventFormTimeState, changeEventFormTime] = useDateTimeState(() => defaultTimeState());

	const {
		repeatState: eventFormRepeatState,
		customRrule: eventFormCustomRrule,
		changeRecurrence: changeEventFormRecurrence,
		repeatOptions: eventFormRepeatOptions,
		setRecurrenceFromRrule,
	} = useRecurrenceOptions({ startDate: eventFormTimeState.start, initialOption: initialData?.repeat });

	useEffect(() => {
		setAdditionalLoading(Boolean(eventToEdit));
		setEventAdditional(null);

		if (!eventToEdit) return;

		fetchQuery<EventFormDetailQuery>(environment, eventFormDetails, { guid: eventToEdit.guid })
			.toPromise()
			.then(response => {
				setAdditionalLoading(false);

				if (response) {
					setEventAdditional(response.event);
				}
			});
	}, [eventToEdit]);

	useEffect(() => {
		if (eventToEdit) {
			setEventFormTitle(eventToEdit.title);
			changeEventFormTime({
				type: "SET",
				newStart: eventToEdit.start,
				newEnd: eventToEdit.end,
				isAllDay: eventToEdit.isAllDay,
			});
		}
	}, [eventToEdit, changeEventFormTime]);

	useEffect(() => {
		const timeout = scrollToImaginaryEvent(250);

		return () => {
			clearTimeout(timeout);
		};
	}, []);

	const eventFormContextValue = useMemo<EventFormContextType>(
		() => ({
			eventTitle: eventFormTitle,
			setEventTitle: setEventFormTitle,
			eventPurpose: eventFormPurpose,
			setEventPurpose: setEventFormPurpose,
			timeState: eventFormTimeState,
			changeTime: changeEventFormTime,
			repeatState: eventFormRepeatState,
			customRrule: eventFormCustomRrule,
			changeRecurrence: changeEventFormRecurrence,
			setRecurrenceFromRrule,
			repeatOptions: eventFormRepeatOptions,
		}),
		[
			eventFormTitle,
			eventFormPurpose,
			eventFormTimeState,
			changeEventFormTime,
			eventFormRepeatState,
			eventFormCustomRrule,
			changeEventFormRecurrence,
			setRecurrenceFromRrule,
			eventFormRepeatOptions,
		],
	);

	const meetWithContextValue = React.useMemo<MeetWithContextType>(
		() => ({
			meetWithParticipants,
			setMeetWithParticipants,
			saveCurrentMeetWithParticipants: noop,
			restorePreviousMeetWithParticipants: noop,
		}),
		[meetWithParticipants],
	);

	const showCalendar = !(inCalendarMode || isMobile);

	return (
		<Modal onClose={onCancel} width={showCalendar ? "90%" : "350px"}>
			<Modal.Header>
				<Modal.Title>{eventAdditional ? "Edit" : "Create"} event</Modal.Title>
				<Modal.HeaderActions onClose={onCancel}>
					{eventToEdit && <IconButton title="Delete event" icon={faTrashCan} onClick={onDeleteEvent} />}
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body noPadding className={styles.modalBody}>
				<EventFormContext.Provider value={eventFormContextValue}>
					<MeetWithContext.Provider value={meetWithContextValue}>
						{showCalendar && (
							<WeekPreview
								viewer={viewer}
								eventToEditGuid={eventAdditional?.guid}
								meetingGuidelinesSettings={meetingGuidelinesSettings}
							/>
						)}
						<div className={cx(styles.panel, { [styles.withCalendar]: showCalendar })}>
							{eventToEdit && (eventAdditional === null || additionalLoading) ? (
								<div className={styles.formContent}>
									<LoadingSpinner dimmer={false} delay={200} />
								</div>
							) : (
								<EventForm
									key={eventAdditional?.guid}
									viewer={viewer}
									meetingGuidelinesSettings={meetingGuidelinesSettings}
									meetingCostSettings={meetingCostSettings}
									eventAdditional={eventAdditional}
									setHasEdits={setHasEdits}
									onFormFinish={onFormFinish}
									hasAccessToSchedulingMeetingAssistant={viewer.hasAccessToScheduledMeetingRecordings}
								/>
							)}
						</div>
					</MeetWithContext.Provider>
				</EventFormContext.Provider>
			</Modal.Body>
		</Modal>
	);
}

type EventFormModalWithoutViewerProps = Omit<EventFormModalProps, "viewer">;

export function EventFormModalWithoutViewer(props: EventFormModalWithoutViewerProps) {
	const { viewer } = useLazyLoadQuery<EventFormModalWithoutViewerQuery>(
		graphql`
			query EventFormModalWithoutViewerQuery {
				viewer {
					...EventFormModal_viewer
				}
			}
		`,
		{},
		{ fetchPolicy: "network-only" },
	);

	return <EventFormModal {...props} viewer={viewer} />;
}
